import { useState, useMemo, useRef, useCallback, useEffect, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox,
  Upload,
  Search,
  X,
  Plane,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Ban,
  FileWarning,
  Layers,
  CloudDownload,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Users,
  Split,
  MoreVertical,
  Trash2,
} from "lucide-react";
import { useToast } from "../../../context/ToastContext";
import { useAppSelector } from "../../../store/hooks";
import { selectUser } from "../../../store/selectors";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import {
  useGetImports,
  useGetImportStatusCounts,
  useUploadImports,
  useSyncFromAirtuerk,
  useAirtuerkConnection,
  useRemoveImport,
} from "../../../api/booking/useImportApi";
import ImportDetailDrawer from "./ImportDetailDrawer";
import s from "./ImportedBookings.module.css";

/* ── Tabs ───────────────────────────────────────────────────────────────────
   Ordered by what needs a human first. "Needs info" leads because that is the
   only bucket where money is waiting on someone. */
const STATUS_TABS = [
  { key: "OPEN", label: "To book", cls: "Open" },
  { key: "All", label: "All", cls: "All" },
  { key: "NEEDS_INFO", label: "Needs info", cls: "NeedsInfo" },
  { key: "READY", label: "Ready", cls: "Ready" },
  { key: "IMPORTED", label: "Imported", cls: "Imported" },
  { key: "IGNORED", label: "Ignored", cls: "Ignored" },
  { key: "FAILED", label: "Failed", cls: "Failed" },
];

const SOURCES = [
  { key: "", label: "All sources" },
  { key: "AER", label: "Cockpit (AER)" },
  { key: "MULTICHECK", label: "Multicheck" },
];

const STATUS_META = {
  NEEDS_INFO: { label: "Needs info", cls: "chipNeedsInfo", Icon: AlertTriangle },
  READY: { label: "Ready", cls: "chipReady", Icon: CheckCircle2 },
  IMPORTED: { label: "Imported", cls: "chipImported", Icon: CheckCircle2 },
  IGNORED: { label: "Ignored", cls: "chipIgnored", Icon: Ban },
  FAILED: { label: "Failed", cls: "chipFailed", Icon: FileWarning },
  SUPERSEDED: { label: "Superseded", cls: "chipSuperseded", Icon: Layers },
};

/**
 * Route as the All Bookings list writes it, so an imported trip and the
 * booking made from it read the same: `A ⇌ B (RT)` for a plain return,
 * `A → B → C (RT-Multi)` once more than two airports are listed, `(OW-M)` for
 * a one-way over a hub, `(OW)` otherwise.
 *
 * Mirrors buildFlightSummary in BookingList.jsx. It works from the already
 * assembled route string rather than legs, which is what the queue carries.
 */
const flightSummary = (route, tripType) => {
  if (!route) return "—";
  const airports = route.split("→").map((a) => a.trim()).filter(Boolean);
  if (airports.length < 2) return route;
  if (tripType === "RT" && airports.length === 2) return `${airports[0]} ⇌ ${airports[1]} (RT)`;
  if (airports.length > 2) return `${airports.join(" → ")} (${tripType === "RT" ? "RT-Multi" : "OW-M"})`;
  return `${airports[0]} → ${airports[1]} (OW)`;
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "—";
  }
};

/** Received-at wants the time too — two files a minute apart are common. */
const formatDateTime = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return `${dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" })} ${dt.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    })}`;
  } catch {
    return "—";
  }
};

/**
 * EUR 1.234,56 — the house format (System Settings §14.5): dot for thousands,
 * comma for decimals, currency code before the amount, always two decimals.
 */
const money = (n, currency = "EUR") => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const formatted = Number(n)
    .toFixed(2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, "#")
    .replace(".", ",")
    .replace(/#/g, ".");
  return `${currency} ${formatted}`;
};

const StatusChip = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, cls: "chipIgnored", Icon: Layers };
  const { Icon } = meta;
  return (
    <span className={`${s.chip} ${s[meta.cls]}`}>
      <Icon size={11} />
      {meta.label}
    </span>
  );
};

/* Column widths. Routing gets the slack, since a four-leg return is the longest
   value on a row. The actions column exists only for someone who can act on a
   row, and takes its room from the columns with the most to spare. */
const COL_WIDTHS = ["10%", "10%", "9%", "26%", "10%", "6%", "13%", "16%"];
const COL_WIDTHS_WITH_ACTIONS = ["10%", "9%", "9%", "24%", "9%", "6%", "12%", "15%", "6%"];

/**
 * Per-row actions. A menu rather than a bare bin icon: removing a trip is not
 * something to do by brushing past it, and there is room here for whatever
 * else the queue grows.
 *
 * Fixed-positioned off the button, like the All Bookings menu, so the table's
 * overflow cannot clip it. It is measured once drawn and opens upward when the
 * row is too close to the bottom of the window for it to fit below.
 */
const RowActionsMenu = ({ row, onRemove }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // A trip that already became a booking keeps its record. Say so here,
  // rather than asking "are you sure?" about something that cannot happen.
  const booked = row.status === "IMPORTED" || Boolean(row.bookingId);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        btnRef.current?.focus();
      }
    };
    // The menu is pinned to a point on the screen, not to the row, so it has
    // to go when whatever is under it moves.
    const onMove = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !menuRef.current || !btnRef.current) return;
    const btn = btnRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const fitsBelow = btn.bottom + 4 + menu.height <= window.innerHeight - 8;
    setPos({
      top: fitsBelow ? btn.bottom + 4 : Math.max(8, btn.top - menu.height - 4),
      left: Math.max(8, btn.right - menu.width),
    });
  }, [open]);

  // Into the menu once it is placed. Not before: a hidden element cannot take focus.
  useEffect(() => {
    if (open && pos) menuRef.current?.querySelector('[role="menuitem"]')?.focus();
  }, [open, pos]);

  const toggle = () => {
    setPos(null);
    setOpen((o) => !o);
  };

  return (
    <div
      className={s.actionsWrap}
      ref={wrapRef}
      onClick={(e) => e.stopPropagation()}
      // Enter and Space belong to the button under focus here. Left to bubble,
      // the row takes them and opens its drawer instead.
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
      }}
      onBlur={(e) => {
        if (open && !e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={btnRef}
        type="button"
        className={s.actionsBtn}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${row.locator || row.fileName || "this booking"}`}
        title="Actions"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div
          ref={menuRef}
          className={s.actionsMenu}
          role="menu"
          style={{
            position: "fixed",
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            visibility: pos ? "visible" : "hidden",
          }}
        >
          <button
            type="button"
            className={s.actionsMenuDanger}
            role="menuitem"
            aria-disabled={booked || undefined}
            onClick={() => {
              if (booked) return;
              setOpen(false);
              onRemove(row);
            }}
          >
            <Trash2 size={13} />
            Remove imported booking
          </button>
          {booked && (
            <p className={s.actionsMenuNote}>
              Already booked, so it cannot be removed. Cancel the booking instead.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * The question, and enough of the trip under it to be sure it is the right
 * one: the reference, the lead traveller, how many files go with it, and when
 * the trip was already keyed in by hand, that the booking itself is not touched.
 */
const removeMessage = (row) => (
  <>
    Are you sure you want to remove this booking?
    <span className={s.removeTrip}>
      <span className={s.mono}>{row.locator || row.fileName || `#${row.id}`}</span>
      {row.leadTraveler && <> · {row.leadTraveler}</>}
      {row.recordCount > 1 && <> · {row.recordCount} files</>}
    </span>
    <span className={s.removeNote}>
      It will be taken off this list and will not come back on the next fetch from Airtuerk.
      {row.existingBooking && ` Booking ${row.existingBooking.bookingReference} is not affected.`}
    </span>
  </>
);

const ImportedBookings = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState("OPEN");
  const [source, setSource] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  // When the API refuses (someone booked the trip since the list loaded), the
  // same dialog turns into the explanation, with no Remove button left to press.
  const [removeBlockMsg, setRemoveBlockMsg] = useState(null);

  // Mirrors the backend can('booking.import.delete') gate, so the Actions
  // column shows exactly when the API will honour it.
  const user = useAppSelector(selectUser);
  const canRemoveImports =
    !!user?.isAdmin ||
    (Array.isArray(user?.permissions) && user.permissions.includes("booking.import.delete"));
  const colWidths = canRemoveImports ? COL_WIDTHS_WITH_ACTIONS : COL_WIDTHS;

  const filters = useMemo(
    () => ({
      status: activeTab === "All" ? "" : activeTab,
      source,
      q: search.trim(),
      dateFrom,
      dateTo,
    }),
    [activeTab, source, search, dateFrom, dateTo]
  );

  const { data: rows, pagination, loading, error, refetch } = useGetImports({ page, limit: 10, ...filters });
  // Counts ignore the status filter on purpose — a tab must show its own total,
  // not the count of whatever tab is currently open.
  const { data: counts, refetch: refetchCounts } = useGetImportStatusCounts({
    source,
    q: search.trim(),
    dateFrom,
    dateTo,
  });
  const { execute: uploadFiles, loading: uploading } = useUploadImports();
  const { execute: removeImport, loading: removing } = useRemoveImport();
  const { execute: syncAirtuerk, loading: syncing } = useSyncFromAirtuerk();
  const connection = useAirtuerkConnection();

  const reload = useCallback(() => {
    refetch();
    refetchCounts();
  }, [refetch, refetchCounts]);

  const hasFilters = Boolean(source || search || dateFrom || dateTo);

  const clearFilters = () => {
    setSource("");
    setSearch("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  /** The main action: pull whatever Airtuerk has waiting for us. */
  const handleSync = async () => {
    const res = await syncAirtuerk();
    if (!res.success) {
      showToast(res.message || "Could not reach Airtuerk", "error");
      return;
    }
    showToast(res.message || "Fetched from Airtuerk", "success");
    reload();
    // One new booking is almost always the reason someone pressed the button,
    // so open it rather than making them find it in the list.
    const created = res.data?.created || [];
    if (created.length === 1) setOpenId(created[0].id);
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    const res = await removeImport(removeTarget.id);
    if (!res.success) {
      // A refusal is worth reading, not just flashing past in a toast. Throwing
      // is how DeleteConfirmModal is told to stay open (it closes itself on a
      // clean return), so the explanation lands where the question was asked.
      const message = res.message || "Could not remove this imported booking";
      setRemoveBlockMsg(message);
      showToast(message, "error");
      // Whatever caused it is news to this list, so bring it up to date behind
      // the dialog.
      reload();
      throw new Error(message);
    }
    showToast(res.message || "Imported booking removed", "success");
    setRemoveTarget(null);
    setRemoveBlockMsg(null);
    // The drawer may be standing open on any file of the trip that just went.
    if ((removeTarget.recordIds || [removeTarget.id]).includes(openId)) setOpenId(null);
    // Removing the only row on a later page would leave it empty.
    if (rows.length === 1 && page > 1) {
      setPage((p) => p - 1);
      refetchCounts();
    } else {
      reload();
    }
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.xml$/i.test(f.name));
    if (files.length === 0) {
      showToast("Only .xml booking files can be imported", "error");
      return;
    }

    const res = await uploadFiles(files);
    if (!res.success) {
      showToast(res.message || "Failed to upload booking files", "error");
      return;
    }

    const { created = [], duplicates = [], failed = [] } = res.data || {};
    // Say what actually happened. "3 uploaded" when two were duplicates and one
    // failed to parse is the kind of message that gets trusted once.
    const parts = [];
    if (created.length) parts.push(`${created.length} imported`);
    if (duplicates.length) parts.push(`${duplicates.length} already received`);
    if (failed.length) parts.push(`${failed.length} could not be read`);
    showToast(parts.join(" · ") || "Nothing to import", failed.length && !created.length ? "error" : "success");

    reload();
    if (created.length === 1) setOpenId(created[0].id);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer?.files);
  };

  const lastReceived = rows?.[0]?.receivedAt;
  const needsInfo = counts?.NEEDS_INFO ?? 0;
  const ready = counts?.READY ?? 0;

  return (
    <div
      className={s.container}
      onDragOver={(e) => {
        e.preventDefault();
        if (!dragging) setDragging(true);
      }}
      onDragLeave={(e) => {
        // Only clear when the pointer actually left the page, not when it
        // crossed from one child element to the next.
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDragging(false);
      }}
      onDrop={onDrop}
    >
      {dragging && (
        <div className={s.dropOverlay}>
          <div className={s.dropCard}>
            <Upload size={30} />
            <div className={s.dropTitle}>Drop Airtuerk XML files</div>
            <div className={s.dropSub}>Cockpit (AER) and multicheck are both recognised automatically</div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className={s.pageHeader}>
        <div className={s.headerLeft}>
          <div className={s.headerIcon}>
            <Inbox size={22} />
          </div>
          <div className={s.headerText}>
            <h1>Imported Bookings</h1>
            <p>Bookings made in the Airtuerk portals, waiting to be brought into SUNSKY</p>
          </div>
        </div>
        <div className={s.headerActions}>
          <button
            className={s.ghostBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            title="Import an XML file by hand — normally you would use Fetch instead"
          >
            <Upload size={14} />
            {uploading ? "Uploading…" : "Upload XML"}
          </button>
          <button className={s.addBtn} onClick={handleSync} disabled={syncing}>
            <CloudDownload size={16} className={syncing ? s.spinning : ""} />
            {syncing ? "Fetching…" : "Fetch from Airtuerk"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xml"
            multiple
            hidden
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      </div>

      {/* ── Where the data comes from, and what needs attention ── */}
      <div className={s.syncStrip}>
        {connection && connection.configured === false ? (
          // Say this plainly rather than letting Fetch fail with a 503 later.
          <div className={s.syncWarn}>
            <AlertTriangle size={13} />
            Airtuerk SFTP is not configured on this server — Fetch will not work until the credentials are set.
          </div>
        ) : (
          <div className={s.syncItem}>
            <span className={`${s.syncDot} ${connection?.ok === false ? s.syncDotBad : ""}`} />
            {connection?.ok === false ? (
              <>Airtuerk unreachable — <strong>{connection.message}</strong></>
            ) : (
              <>
                Last file received <strong>{formatDateTime(lastReceived)}</strong>
                {connection?.xmlFiles > 0 && (
                  <span className={s.syncWaiting}>{connection.xmlFiles} waiting on Airtuerk</span>
                )}
              </>
            )}
          </div>
        )}

        <button className={s.syncRefresh} onClick={reload} disabled={loading}>
          <RefreshCw size={12} className={loading ? s.spinning : ""} />
          Refresh list
        </button>
          {needsInfo > 0 && (
            <button className={s.syncAlert} onClick={() => { setActiveTab("NEEDS_INFO"); setPage(1); }}>
              <AlertTriangle size={13} />
              <strong>{needsInfo}</strong> need{needsInfo === 1 ? "s" : ""} information before booking
            </button>
          )}
        {ready > 0 && (
          <button className={s.syncReady} onClick={() => { setActiveTab("READY"); setPage(1); }}>
            <CheckCircle2 size={13} />
            <strong>{ready}</strong> ready to book
          </button>
        )}
      </div>

      {/* ── Tabs ── */}
      <div className={s.tabBar}>
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const count = counts?.[tab.key] ?? 0;
          return (
            <button
              key={tab.key}
              className={`${s.tab} ${s[`tab${tab.cls}`] || ""} ${isActive ? s.tabActive : ""}`}
              onClick={() => {
                setActiveTab(tab.key);
                setPage(1);
              }}
            >
              {tab.label}
              <span className={`${s.tabBadge} ${!isActive ? s[`badge${tab.cls}`] || "" : ""}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* ── Filters ── */}
      <div className={s.filterBar}>
        <div className={s.searchWrap}>
          <Search size={14} className={s.searchIcon} />
          <input
            type="text"
            className={s.searchInput}
            placeholder="Search supplier ref, booking no, file name…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          {search && (
            <button className={s.searchClear} onClick={() => setSearch("")}>
              <X size={12} />
            </button>
          )}
        </div>

        <div className={s.filterDivider} />

        <div className={s.segmented}>
          {SOURCES.map((opt) => (
            <button
              key={opt.key}
              className={`${s.segment} ${source === opt.key ? s.segmentActive : ""}`}
              onClick={() => {
                setSource(opt.key);
                setPage(1);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className={s.dateRangeWrap} title="Received between">
          <input
            type="date"
            className={s.dateInput}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
          />
          <span className={s.dateSeparator}>–</span>
          <input
            type="date"
            className={s.dateInput}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {hasFilters && (
          <button className={s.clearFilters} onClick={clearFilters}>
            <X size={12} />
            Clear filters
          </button>
        )}
      </div>

      {/* ── Table ── */}
      <div className={s.tableWrap}>
        <table className={s.table}>
          <colgroup>
            {colWidths.map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th>Received</th>
              <th>Source</th>
              <th>Supplier ref</th>
              <th>Routing</th>
              <th>Travel</th>
              <th>Pax</th>
              <th>Cost / Sell</th>
              <th>Status</th>
              {canRemoveImports && <th className={s.thActions}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={colWidths.length}>
                  <div className={s.skeletonWrap}>
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className={s.skeletonRow} />
                    ))}
                  </div>
                </td>
              </tr>
            )}

            {!loading && error && (
              <tr>
                <td colSpan={colWidths.length}>
                  <div className={s.emptyState}>
                    <FileWarning size={38} className={s.emptyIcon} />
                    <p className={s.emptyTitle}>{error}</p>
                    <button className={s.ghostBtn} onClick={reload}>
                      <RefreshCw size={14} />
                      Try again
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {!loading && !error && rows.length === 0 && (
              <tr>
                <td colSpan={colWidths.length}>
                  <div className={s.emptyState}>
                    <Inbox size={38} className={s.emptyIcon} />
                    <p className={s.emptyTitle}>
                      {hasFilters || activeTab !== "All" ? "Nothing matches these filters" : "No bookings imported yet"}
                    </p>
                    <p className={s.emptySubtitle}>
                      {hasFilters || activeTab !== "All"
                        ? "Try clearing the filters or another tab."
                        : "Drop an Airtuerk XML file anywhere on this page, or use Upload XML."}
                    </p>
                  </div>
                </td>
              </tr>
            )}

            {!loading &&
              !error &&
              rows.map((row) => (
                // Rows open the review drawer, so they need to behave like the
                // buttons they are — reachable by Tab and openable by Enter.
                <tr
                  key={row.id}
                  className={s.row}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open imported booking ${row.locator || row.fileName || row.id}`}
                  onClick={() => setOpenId(row.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenId(row.id);
                    }
                  }}
                >
                  <td className={s.muted}>{formatDateTime(row.receivedAt)}</td>
                  <td>
                    <span className={`${s.sourceChip} ${row.source === "AER" ? s.sourceAer : s.sourceMc}`}>
                      {row.source === "AER" ? "Cockpit" : "Multicheck"}
                    </span>
                  </td>
                  <td>
                    <span className={s.mono}>{row.locator || "—"}</span>
                    {row.recordCount > 1 && (
                      <span className={s.recordCount} title={`One trip, sent as ${row.recordCount} files`}>
                        {row.recordCount} files
                      </span>
                    )}
                  </td>
                  <td>
                    <div className={s.routeCell}>
                      <Plane size={12} className={s.routeIcon} />
                      <span className={s.routeText} title={row.route || ""}>
                        {flightSummary(row.route, row.tripType)}
                      </span>
                      {row.split && (
                        <span
                          className={s.splitBadge}
                          title={
                            row.airlinePnrs?.length
                              ? `Ticketed separately — airline PNRs ${row.airlinePnrs.join(" and ")}`
                              : "Ticketed separately per direction"
                          }
                        >
                          <Split size={10} /> Split Flight
                        </span>
                      )}
                    </div>
                    {row.leadTraveler && <div className={s.subText}>{row.leadTraveler}</div>}
                  </td>
                  <td className={s.muted}>{formatDate(row.travelDate)}</td>
                  <td>
                    <span className={s.paxCell}>
                      <Users size={11} />
                      {row.passengers ?? 0}
                    </span>
                  </td>
                  <td>
                    <div className={s.priceCell}>
                      <span className={row.cost === null ? s.priceMissing : s.priceCost}>
                        {row.cost === null ? "cost —" : money(row.cost, row.currency)}
                      </span>
                      <span className={row.sell === null ? s.priceMissing : s.priceSell}>
                        {row.sell === null ? "sell —" : money(row.sell, row.currency)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className={s.statusCell}>
                      <StatusChip status={row.status} />
                      {row.blockerCount > 0 && row.status === "NEEDS_INFO" && (
                        <span className={s.blockerCount}>{row.blockerCount}</span>
                      )}
                      {row.existingBooking && (
                        <button
                          className={s.alreadyChip}
                          title={`Already booked as ${row.existingBooking.bookingReference}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/bookings/${row.existingBooking.bookingId}`);
                          }}
                        >
                          already booked
                        </button>
                      )}
                      {row.bookingId && (
                        <button
                          className={s.linkBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/bookings/${row.bookingId}`);
                          }}
                          title="Open the booking this became"
                        >
                          <ExternalLink size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                  {canRemoveImports && (
                    <td className={s.tdActions}>
                      <RowActionsMenu
                        row={row}
                        onRemove={(r) => {
                          setRemoveBlockMsg(null);
                          setRemoveTarget(r);
                        }}
                      />
                    </td>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {pagination.totalPages > 1 && (
        <div className={s.paginationBar}>
          <div className={s.paginationInfo}>
            Showing {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </div>
          <div className={s.pageNumbers}>
            <button className={s.pageNavBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === pagination.totalPages || Math.abs(n - page) <= 1)
              .map((n, idx, arr) => (
                <span key={n} className={s.pageSlot}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span className={s.pageEllipsis}>…</span>}
                  <button
                    className={`${s.pageBtn} ${n === page ? s.pageBtnActive : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </span>
              ))}
            <button
              className={s.pageNavBtn}
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        open={Boolean(removeTarget)}
        onClose={() => {
          setRemoveTarget(null);
          setRemoveBlockMsg(null);
        }}
        title={removeBlockMsg ? "Cannot remove this booking" : "Remove imported booking"}
        message={removeBlockMsg || (removeTarget ? removeMessage(removeTarget) : null)}
        confirmLabel="Remove"
        loadingLabel="Removing…"
        hideConfirm={Boolean(removeBlockMsg)}
        cancelLabel={removeBlockMsg ? "Close" : "Cancel"}
        loading={removing}
        onConfirm={handleRemove}
      />

      <ImportDetailDrawer
        importId={openId}
        onClose={() => setOpenId(null)}
        onChanged={reload}
        onOpenRelated={(id) => setOpenId(id)}
      />
    </div>
  );
};

export default ImportedBookings;
